import { useEffect, useRef } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../app/providers/AuthProvider";
import { useNotifications } from "../app/providers/NotificationProvider";
import { UserProfile } from "../features/auth/UserProfile";
import { Icon, type IconName } from "./Icon";

const navItems: Array<{ to: string; label: string; icon: IconName }> = [
  { to: "/", label: "Главная", icon: "home" },
  { to: "/events", label: "События", icon: "calendar" },
  { to: "/notifications", label: "Напоминания", icon: "bell" },
  { to: "/bookings", label: "Мои брони", icon: "gift" },
  { to: "/family", label: "Семья", icon: "family" },
  { to: "/profile", label: "Профиль", icon: "settings" },
];

const mobilePrimaryNavItems = [
  navItems[0]!,
  navItems[1]!,
  navItems[3]!,
  navItems[4]!,
];
const mobileMoreNavItems = [navItems[2]!, navItems[5]!];

const pageTitles: Record<string, string> = {
  "/": "Главная",
  "/bookings": "Мои брони",
  "/events": "События",
  "/notifications": "Напоминания",
  "/family": "Семья",
  "/profile": "Профиль",
  "/families/new": "Создать семью",
};

function getPageTitle(pathname: string) {
  if (/^\/members\/[^/]+\/wishes\/new$/.test(pathname)) return "Добавить желание";
  if (/^\/members\/[^/]+\/wishes$/.test(pathname)) return "Желания";
  return pageTitles[pathname] ?? "Family Gifts";
}

function Brand() {
  return (
    <NavLink to="/" className="flex items-center gap-2.5 text-[17px] font-extrabold tracking-[-0.03em] text-[#171714]">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#171714] text-[#FF7A00]">
        <Icon name="gift" size={19} />
      </span>
      Family Gifts
    </NavLink>
  );
}

function UnreadBadge({ count, compact = false }: { count: number; compact?: boolean }) {
  if (count < 1) return null;
  return (
    <span
      aria-label={`${count} непрочитанных напоминаний`}
      className={
        compact
          ? "absolute right-1 top-1 min-w-4 rounded-full bg-[#FF7A00] px-1 text-center text-[9px] font-extrabold leading-4 text-white ring-2 ring-[#1B1B18]"
          : "ml-auto min-w-6 rounded-full bg-[#FF7A00] px-1.5 text-center text-[11px] font-extrabold leading-6 text-white"
      }
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const { state: notificationState } = useNotifications();
  const { pathname } = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const unreadCount =
    notificationState.status === "loaded" ? notificationState.page.unread_count : 0;
  const isMoreActive = mobileMoreNavItems.some((item) => item.to === pathname);

  useEffect(() => {
    const pageTitle = getPageTitle(pathname);
    document.title = pageTitle === "Family Gifts" ? pageTitle : `${pageTitle} — Family Gifts`;
    mainRef.current?.focus();
  }, [pathname]);

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[520px] overflow-hidden bg-[#F8F7F2] md:min-h-full md:rounded-[32px] md:shadow-[0_24px_80px_rgba(28,27,22,0.16)] lg:max-w-[1440px] lg:rounded-[36px]">
      <a
        href="#main-content"
        className="sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:not-sr-only focus:rounded-xl focus:bg-white focus:px-4 focus:py-3 focus:text-sm focus:font-bold focus:text-[#171714] focus:shadow-lg focus:outline-2 focus:outline-offset-2 focus:outline-[#FF7A00]"
      >
        Перейти к содержимому
      </a>
      <aside className="hidden w-[260px] shrink-0 flex-col border-r border-black/[0.05] bg-white px-5 py-6 lg:flex xl:w-[288px] xl:px-7">
        <Brand />

        <nav className="mt-12 flex flex-col gap-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-2xl px-4 py-3.5 text-sm font-semibold transition-[background-color,color,box-shadow] ${
                  isActive
                    ? "bg-[#171714] text-white shadow-[0_10px_24px_rgba(23,23,20,0.16)]"
                    : "text-[#77776F] hover:bg-[#F2F1EC] hover:text-[#171714]"
                }`
              }
            >
              <Icon name={item.icon} size={21} />
              {item.label}
              {item.to === "/notifications" && <UnreadBadge count={unreadCount} />}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto rounded-[22px] bg-[#F2F1EC] p-3">
          <div className="flex items-center gap-3">
            <UserProfile user={user} showName />
          </div>
          <button type="button" onClick={logout} className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-white px-3 py-2.5 text-xs font-semibold text-[#77776F] transition-colors hover:text-[#171714]">
            <Icon name="logout" size={16} />
            Выйти
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between px-5 pb-3 pt-5 lg:hidden">
          <Brand />
          <div className="flex items-center gap-2">
            <UserProfile user={user} />
            <button type="button" onClick={logout} className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-[#77776F] transition-colors hover:bg-white hover:text-[#171714]" aria-label="Выйти">
              <Icon name="logout" size={18} />
            </button>
          </div>
        </header>

        <main
          id="main-content"
          ref={mainRef}
          tabIndex={-1}
          className="flex flex-1 flex-col overflow-y-auto pb-[calc(6rem+env(safe-area-inset-bottom))] outline-none lg:pb-0"
        >
          <Outlet />
        </main>

        <nav aria-label="Основная навигация" className="fixed inset-x-0 bottom-0 z-20 mx-auto w-full max-w-[520px] px-3 pb-[max(12px,env(safe-area-inset-bottom))] lg:hidden">
          <div className="rounded-[22px] border border-black/5 bg-[#1B1B18]/95 p-1.5 shadow-[0_12px_32px_rgba(22,22,18,0.25)] backdrop-blur-xl">
            <div className="grid grid-cols-5">
              {mobilePrimaryNavItems.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.to === "/"} className={({ isActive }) => `relative flex min-w-0 flex-col items-center gap-1 rounded-[17px] px-1 py-2 text-[11px] font-semibold leading-4 transition-colors ${isActive ? "bg-[#FF7A00] text-white" : "text-white/55 hover:text-white"}`}>
                  <Icon name={item.icon} size={20} />
                  <span className="whitespace-nowrap">{item.label === "Мои брони" ? "Брони" : item.label}</span>
                </NavLink>
              ))}
              <details className="group relative min-w-0">
                <summary className={`flex h-full cursor-pointer list-none flex-col items-center justify-center gap-1 rounded-[17px] px-1 py-2 text-[11px] font-semibold leading-4 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF7A00] [&::-webkit-details-marker]:hidden ${isMoreActive ? "bg-[#FF7A00] text-white" : "text-white/55 hover:text-white"}`}>
                  <Icon name="more-horizontal" size={20} />
                  <span className="whitespace-nowrap">Ещё</span>
                </summary>

                <div className="absolute bottom-[calc(100%+1.125rem)] right-0 hidden w-[min(12rem,calc(100vw-1.5rem))] flex-col gap-1 rounded-2xl border border-black/5 bg-[#1B1B18]/[.98] p-2 shadow-[0_12px_32px_rgba(22,22,18,0.25)] group-open:flex">
                  {mobileMoreNavItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={(event) => {
                        const details = event.currentTarget.closest("details");
                        if (details) details.open = false;
                      }}
                      className={({ isActive }) => `relative flex min-w-0 items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-[#FF7A00] text-white" : "text-white/75 hover:bg-white/10 hover:text-white"}`}
                    >
                      <Icon name={item.icon} size={19} />
                      <span className="min-w-0 flex-1">{item.label}</span>
                      {item.to === "/notifications" && <UnreadBadge count={unreadCount} />}
                    </NavLink>
                  ))}
                </div>
              </details>
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
}
